// setCanvasPreview.js

/**
 * Draw a pixel-based crop from an <img> into a <canvas>, crisply (DPR-aware).
 *
 * @param {HTMLImageElement} image
 * @param {HTMLCanvasElement} canvas
 * @param {{ x:number, y:number, width:number, height:number }} crop  // pixel crop (natural image space)
 * @param {{ pixelRatio?: number, backgroundColor?: string }} [opts]
 *   - pixelRatio: defaults to window.devicePixelRatio || 1
 *   - backgroundColor: e.g. "#fff" to avoid transparent background when later exporting JPEG
 */
const setCanvasPreview = (image, canvas, crop, opts = {}) => {
  if (!image || !canvas || !crop) return;

  const pixelRatio =
    typeof opts.pixelRatio === "number" && opts.pixelRatio > 0
      ? opts.pixelRatio
      : (typeof window !== "undefined" && window.devicePixelRatio) || 1;

  // crop is already in NATURAL pixels (because you built it with convertToPixelCrop(...naturalWidth/...))
  const { x, y, width, height } = crop;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No 2D context");

  // Set the canvas size in device pixels for sharpness
  canvas.width = Math.max(1, Math.floor(width * pixelRatio));
  canvas.height = Math.max(1, Math.floor(height * pixelRatio));

  // Reset any existing transforms before applying new ones
  ctx.setTransform(1, 0, 0, 1, 0, 0);

  // High quality scaling
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  // Apply DPR scaling so 1 canvas unit == 1 device pixel
  ctx.scale(pixelRatio, pixelRatio);

  // Optional background fill (useful when you plan to export JPEG)
  if (opts.backgroundColor) {
    ctx.save();
    ctx.fillStyle = opts.backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
  }

  // Move the crop area so it lands at canvas (0,0)
  ctx.save();
  ctx.translate(-x, -y);

  // Draw the full image; due to translate, only the crop lands in the canvas
  ctx.drawImage(
    image,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight
  );

  ctx.restore();
  // Note: drawing is sync; function returns void
};

export default setCanvasPreview;

/**
 * Helper: promisified canvas.toBlob for convenient exporting.
 * @param {HTMLCanvasElement} canvas
 * @param {string} [type="image/png"] e.g. "image/jpeg"
 * @param {number} [quality=0.92] 0..1 (used for JPEG/WebP)
 * @returns {Promise<Blob>}
 */
export function canvasToBlob(canvas, type = "image/png", quality = 0.92) {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("canvas.toBlob() produced null"));
      }, type, quality);
    } catch (e) {
      reject(e);
    }
  });
}